"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useReducedMotion } from "framer-motion";
import {
  Activity,
  ArrowLeftRight,
  ArrowRight,
  ChevronRight,
  CircleDollarSign,
  Flame,
  LockOpen,
  Percent,
  Radar,
  Search,
  Sprout,
  Vote,
  type LucideIcon,
} from "lucide-react";

import { DATA_CATALOG, MARKET_TOPICS } from "@/config/dashboard";
import { trackEvent } from "@/lib/analytics";
import { useSession } from "@/lib/auth-client";
import { LANDING_ASK_KEY } from "@/lib/paywall";
import { cn } from "@/lib/utils";
import { useMarketTopics } from "@/hooks/use-market-data";
import { Button } from "@/components/ui/button";
import { RadarDot } from "@/components/marketing/animate-on-scroll";

// Each question leans on a feed listed beside it, plus one stock, so the box
// reads as a way into Fexr's data rather than a general chatbot.
const EXAMPLE_QUESTIONS = [
  "How are top traders positioned on BTC?",
  "Which token unlocks land this month?",
  "Where is funding most crowded right now?",
  "What's moving NVDA this week?",
];

// Public pages that are not hourly topics; plain links so crawlers reach them.
const MORE_PUBLIC_DATA = ["markets", "whaleBoardPage", "strategies"].map(
  (id) => {
    const d = DATA_CATALOG.find((x) => x.id === id)!;
    return { href: d.publicHref!, title: d.title };
  },
);

function ago(iso: string | null | undefined, now: number): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const mins = Math.max(0, Math.round((now - t) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
}

function StepLabel({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="font-figure tabular-nums text-primary">0{n}</span>
      {children}
    </span>
  );
}

// One icon per feed, picked for what the feed measures.
const TOPIC_ICONS: Record<string, LucideIcon> = {
  "funding-rates": Percent, // a yearly rate longs and shorts pay each other
  "token-unlocks": LockOpen, // locked supply becoming sellable
  stablecoins: CircleDollarSign, // dollars on-chain, minted and redeemed
  "staking-yields": Sprout, // deposits that grow
  governance: Vote, // token-holder votes
  "large-transfers": ArrowLeftRight, // $250K+ moving between wallets
  trending: Flame, // unusual moves right now
};

// The scan steps one row per second: the feeds are read on a schedule, the
// prices row above them on every tick (fexrapi price_watcher).
const SCAN_MS = 1000;

/** A feed row: icon, name, this hour's headline, when it was read. */
function WatchRow({
  href,
  icon: Icon,
  title,
  line,
  when,
  scanning,
  hidden,
}: {
  href: string;
  icon: LucideIcon;
  title: string;
  line: string;
  when: React.ReactNode;
  scanning: boolean;
  hidden?: boolean;
}) {
  return (
    <li className="border-b border-border/60 last:border-0">
      <Link href={href} className="group flex items-center gap-3 py-2.5">
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors duration-2",
            scanning
              ? "border-primary/40 bg-primary/15 text-primary"
              : "border-border bg-foreground/[0.03] text-muted-foreground group-hover:text-foreground",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-foreground">{title}</span>
          <span
            className={cn(
              "block truncate text-xs text-muted-foreground",
              hidden && "opacity-0",
            )}
          >
            {line}
          </span>
        </span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {when}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-colors duration-1 group-hover:text-foreground" />
      </Link>
    </li>
  );
}

/**
 * Step 1: what Fexr watches. Prices on every tick (with every stop and target
 * checked on each), then the hourly feeds with this hour's headline and when
 * each was read.
 */
function WatchList() {
  const { lines, loaded } = useMarketTopics();
  const reduce = useReducedMotion();
  const [scan, setScan] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const rows = MARKET_TOPICS.length + 1;

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
      if (!reduce) setScan((i) => (i + 1) % rows);
    }, SCAN_MS);
    return () => window.clearInterval(id);
  }, [reduce, rows]);

  return (
    <div className="flex h-full flex-col rounded-lg border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <StepLabel n={1}>Fexr watches</StepLabel>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <RadarDot size={6} />
          Live
        </span>
      </div>

      <ul className="mt-3 flex-1">
        <WatchRow
          href="/markets"
          icon={Activity}
          title="Prices"
          line="Every tick, with each stop-loss and take-profit checked on it"
          when={<span className="text-success">every second</span>}
          scanning={!reduce && scan === 0}
        />
        {MARKET_TOPICS.map((t, i) => {
          const live = lines[t.slug];
          return (
            <WatchRow
              key={t.slug}
              href={`/markets/${t.slug}`}
              icon={TOPIC_ICONS[t.slug] ?? Radar}
              title={t.title}
              line={
                live?.headline ??
                t.description.replace(/(, updated hourly)?\.$/, "")
              }
              hidden={!loaded}
              when={ago(live?.as_of, now) ?? "hourly"}
              scanning={!reduce && scan === i + 1}
            />
          );
        })}
      </ul>

      <p className="mt-3 text-xs text-muted-foreground">
        Also open, no sign-in:{" "}
        {MORE_PUBLIC_DATA.map((l, i) => (
          <span key={l.href}>
            {i > 0 && " · "}
            <Link
              href={l.href}
              className="text-foreground/80 underline-offset-4 transition-colors duration-1 hover:text-foreground hover:underline"
            >
              {l.title}
            </Link>
          </span>
        ))}
      </p>
    </div>
  );
}

/**
 * Landing section: Watch → Ask → Act. The left card is the data Fexr reads
 * every hour (live headlines from GET /v1/markets/topics); the right column is
 * Ask, framed as a question over that data. A typed question is cached in
 * localStorage and the visitor is sent to sign in; Discover picks it up on
 * arrival and answers it, so the first screen after sign-in is their answer.
 */
export function MarketDataShowcaseClient() {
  const { status } = useSession();
  const router = useRouter();
  const [question, setQuestion] = useState("");
  const isAuthed = status === "authenticated";

  const submit = (raw: string, exampleIndex?: number) => {
    const q = raw.trim();
    if (!q) return;
    trackEvent("landing.ask_submitted", {
      origin: exampleIndex === undefined ? "typed" : "example",
      example_index: exampleIndex,
      authed: isAuthed,
    });
    let discover = "/dashboard";
    try {
      localStorage.setItem(
        LANDING_ASK_KEY,
        JSON.stringify({ question: q, at: Date.now() }),
      );
    } catch {
      // Storage disabled: fall back to pre-filling Discover through the URL.
      discover = `/dashboard?q=${encodeURIComponent(q)}`;
    }
    router.push(
      isAuthed ? discover : `/register?from=${encodeURIComponent(discover)}`,
    );
  };

  return (
    <div className="grid gap-4 font-small lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <WatchList />

      <div className="flex flex-col gap-4">
        {/* Step 2: Ask over the same data */}
        <div className="rounded-lg border border-border bg-card p-4 sm:p-5">
          <StepLabel n={2}>You ask</StepLabel>
          <p className="mt-2 text-sm text-foreground">
            Any stock, ETF, commodity or token. The answer is built from these
            feeds and Fexr&apos;s market data, and every figure shows its source
            and how old it is.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(question);
            }}
            className="mt-4 flex items-center gap-2 rounded-md border border-border bg-background/60 p-1 pl-3 transition-colors duration-1 focus-within:border-primary/40"
          >
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={1000}
              aria-label="Ask about any market"
              placeholder="Ask about any market"
              className="h-8 min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            <Button
              type="submit"
              disabled={!question.trim()}
              className="shrink-0"
            >
              {isAuthed ? "Ask" : "Sign in to ask"}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </form>
          <ul className="mt-3">
            {EXAMPLE_QUESTIONS.map((q, i) => (
              <li key={q}>
                <button
                  type="button"
                  onClick={() => submit(q, i)}
                  className="group flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors duration-1 hover:bg-foreground/[0.03] hover:text-foreground"
                >
                  {q}
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity duration-1 group-hover:opacity-100" />
                </button>
              </li>
            ))}
          </ul>
          {!isAuthed && (
            <p className="mt-2 px-2 text-xs text-muted-foreground">
              Your question is kept while you sign in and answered when you
              arrive.
            </p>
          )}
        </div>

        {/* Step 3: Act, inside vault rules */}
        <a
          href="#vaults"
          className="group flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4 transition-colors duration-1 hover:border-primary/40 sm:p-5"
        >
          <span className="min-w-0">
            <StepLabel n={3}>Agents act</StepLabel>
            <span className="mt-2 block text-sm text-foreground">
              In a vault, agents trade on your behalf, only inside the rules its
              members set.
            </span>
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors duration-1 group-hover:text-foreground" />
        </a>
      </div>
    </div>
  );
}
